import BrandMark from "../components/brand/BrandMark";
import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { 
  Car, Users, CreditCard, QrCode, LogOut, 
  BarChart3, ArrowLeft, Edit, Phone, User,
  AlertTriangle, Save, Loader2, Settings, 
  Ban, CheckCircle, Eye, RefreshCw, ZoomIn, FileText
} from "lucide-react";
import { Textarea } from "../components/ui/textarea";

const API = '/api';

// Helper function to safely get phone/contact value
const getContactValue = (contact) => {
  if (!contact) return "-";
  if (typeof contact === "string") return contact;
  if (typeof contact === "object") {
    return contact.phone || contact.whatsapp || "-";
  }
  return "-";
};

const AdminUserDetail = () => {
  const { userId } = useParams();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  
  // Edit user dialog
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [editUserData, setEditUserData] = useState({});
  const [savingUser, setSavingUser] = useState(false);
  
  // Edit vehicle dialog
  const [editVehicleOpen, setEditVehicleOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [editVehicleData, setEditVehicleData] = useState({});
  const [savingVehicle, setSavingVehicle] = useState(false);
  
  // User payments
  const [payments, setPayments] = useState([]);
  const [qrRotations, setQrRotations] = useState([]);
  const [qrGate, setQrGate] = useState(null);
  const [canonical, setCanonical] = useState(null);
  const [loadingPayments, setLoadingPayments] = useState(false);
  
  // Payment review dialog
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [reviewing, setReviewing] = useState(false);
  
  // Voucher dialog
  const [voucherDialogOpen, setVoucherDialogOpen] = useState(false);
  const [voucherUrl, setVoucherUrl] = useState(null);
  
  // Block/unblock
  const [blocking, setBlocking] = useState(false);

  useEffect(() => {
    fetchUserDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchUserDetail = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}`);
      setUserData(response.data.user);
      setVehicles(response.data.vehicles);
      // Also fetch user's payments
      fetchUserPayments();
      axios.get(`${API}/admin/uc/users/${userId}/qr-rotations`)
        .then((r) => setQrRotations(r.data?.rotations || []))
        .catch(() => setQrRotations([]));
      axios.get(`${API}/admin/uc/users/${userId}`)
        .then((r) => { setQrGate(r.data?.qr_gate || null); setCanonical(r.data || null); })
        .catch(() => { setQrGate(null); setCanonical(null); });
    } catch (error) {
      toast.error("Error al cargar datos del usuario");
      navigate("/admin/users");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPayments = async () => {
    setLoadingPayments(true);
    try {
      const response = await axios.get(`${API}/admin/payments`);
      // Filter payments for this user
      const userPayments = response.data.filter(p => p.user_id === userId);
      setPayments(userPayments);
    } catch (error) {
      console.error("Error fetching payments:", error);
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleBlockUser = async () => {
    setBlocking(true);
    try {
      const newStatus = !userData.is_active;
      await axios.put(`${API}/admin/users/${userId}/block`, { is_active: newStatus });
      toast.success(newStatus ? "Usuario activado" : "Usuario bloqueado");
      fetchUserDetail();
    } catch (error) {
      toast.error("Error al cambiar estado del usuario");
    } finally {
      setBlocking(false);
    }
  };

  const handleViewVoucher = async (paymentId) => {
    try {
      const response = await axios.get(`${API}/admin/payments/${paymentId}/voucher`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      setVoucherUrl(url);
      setVoucherDialogOpen(true);
    } catch (error) {
      toast.error("Error al cargar voucher");
    }
  };

  const openReviewPayment = (payment) => {
    setSelectedPayment(payment);
    setAdminNotes("");
    setRejectionReason("");
    setReviewDialogOpen(true);
  };

  const submitReview = async (status) => {
    if (status === "rejected" && !rejectionReason.trim()) {
      toast.error("Por favor ingresa una razón de rechazo");
      return;
    }
    
    setReviewing(true);
    try {
      await axios.put(`${API}/admin/payments/${selectedPayment.id}/review`, {
        status,
        admin_notes: adminNotes || null,
        rejection_reason: status === "rejected" ? rejectionReason : null
      });
      toast.success(`Pago ${status === "approved" ? "aprobado" : "rechazado"}`);
      setReviewDialogOpen(false);
      fetchUserDetail();
      fetchUserPayments();
    } catch (error) {
      toast.error("Error al procesar revisión");
    } finally {
      setReviewing(false);
    }
  };

  const openEditUser = () => {
    setEditUserData({
      full_name: userData.full_name || "",
      phone: userData.phone || "",
      whatsapp: userData.whatsapp || ""
    });
    setEditUserOpen(true);
  };

  const saveUser = async () => {
    setSavingUser(true);
    try {
      await axios.put(`${API}/admin/users/${userId}`, editUserData);
      toast.success("Usuario actualizado correctamente");
      setEditUserOpen(false);
      fetchUserDetail();
    } catch (error) {
      toast.error("Error al actualizar usuario");
    } finally {
      setSavingUser(false);
    }
  };

  const openEditVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    // Handle primary_contact being either string or object
    const primaryContact = typeof vehicle.primary_contact === "object" 
      ? (vehicle.primary_contact?.phone || vehicle.primary_contact?.whatsapp || "")
      : (vehicle.primary_contact || "");
    
    setEditVehicleData({
      name: vehicle.name || "",
      plate_number: vehicle.plate_number || "",
      primary_contact: primaryContact
    });
    setEditVehicleOpen(true);
  };
  const saveVehicle = async () => {
    if (!selectedVehicle) return;
    setSavingVehicle(true);
    try {
      await axios.put(`${API}/admin/vehicles/${selectedVehicle.id}`, editVehicleData);
      toast.success("Vehículo actualizado correctamente. Los cambios se reflejan inmediatamente en el QR.");
      setEditVehicleOpen(false);
      fetchUserDetail();
    } catch (error) {
      toast.error("Error al actualizar vehículo");
    } finally {
      setSavingVehicle(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B2545] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B2545]">
      {/* Sidebar */}


      {/* Main Content */}
      <main className="p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Link to="/admin/users" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-4">
              <ArrowLeft className="w-4 h-4" />
              Volver a Usuarios
            </Link>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {userData?.full_name}
                </h1>
                <p className="text-slate-400 mt-1">{userData?.email}</p>
              </div>
              <Button onClick={openEditUser} className="bg-[#1B84FF] hover:bg-[#0F6FDB]">
                <Edit className="w-4 h-4 mr-2" />
                Editar Usuario
              </Button>
            </div>
          </div>

          {/* User Info Card */}
          <Card className="bg-[#14375F] border-white/15 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="w-5 h-5" />
                Información del Usuario
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400">Nombre Completo</Label>
                <p className="text-white font-medium">{userData?.full_name}</p>
              </div>
              <div>
                <Label className="text-slate-400">Email</Label>
                <p className="text-white font-medium">{userData?.email}</p>
              </div>
              <div>
                <Label className="text-slate-400">Teléfono</Label>
                <p className="text-white font-mono">{userData?.phone || "-"}</p>
              </div>
              <div>
                <Label className="text-slate-400">WhatsApp</Label>
                <p className="text-white font-mono">{userData?.whatsapp || "-"}</p>
              </div>
              <div>
                <Label className="text-slate-400">Estado</Label>
                <Badge className={userData?.is_active ? "bg-[#1B84FF]/20 text-[#5BC0FF]" : "bg-red-500/20 text-red-400"}>
                  {userData?.is_active ? "Activo" : "Bloqueado"}
                </Badge>
              </div>
              <div>
                <Label className="text-slate-400">Registro</Label>
                <p className="text-white">{userData?.created_at ? new Date(userData.created_at).toLocaleDateString() : "-"}</p>
              </div>
            </CardContent>
          </Card>

          {/* User Actions Card */}
          <Card className="bg-[#14375F] border-white/15 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Acciones de Usuario
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-4">
              <Button
                onClick={handleBlockUser}
                disabled={blocking}
                variant="outline"
                className={userData?.is_active 
                  ? "border-red-500 text-red-400 hover:bg-red-500/20" 
                  : "border-emerald-500 text-[#5BC0FF] hover:bg-[#1B84FF]/20"
                }
              >
                {blocking ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : userData?.is_active ? (
                  <Ban className="w-4 h-4 mr-2" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                {userData?.is_active ? "Bloquear Usuario" : "Activar Usuario"}
              </Button>
              
              <Button
                onClick={() => navigate(`/admin/payments?search=${encodeURIComponent(userData?.email || "")}`)}
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Ver Pagos en Panel
              </Button>
            </CardContent>
          </Card>

          {/* User Payments */}
          <Card className="bg-[#14375F] border-white/15 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Pagos del Usuario ({payments.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingPayments ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#5BC0FF]" />
                </div>
              ) : payments.length === 0 ? (
                <div className="text-center py-8">
                  <CreditCard className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">Este usuario no tiene pagos registrados</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {payments.map((payment) => (
                    <div key={payment.id} className="bg-slate-700/50 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={
                            payment.status === "approved" ? "bg-[#1B84FF]/20 text-[#5BC0FF]" :
                            payment.status === "rejected" ? "bg-red-500/20 text-red-400" :
                            "bg-amber-500/20 text-amber-400"
                          }>
                            {payment.status === "approved" ? "Aprobado" :
                             payment.status === "rejected" ? "Rechazado" : "Pendiente"}
                          </Badge>
                          <span className="text-white font-semibold">{payment.currency === "INR" ? "₹" : "S/ "}{payment.amount}</span>
                          <span className="text-slate-400 text-sm">
                            {payment.method?.toUpperCase()}{payment.plan_months ? ` · ${payment.plan_months} meses` : ""}
                          </span>
                          {payment.country_mismatch && (
                            <Badge className="bg-red-500/20 text-red-400 text-[10px]">⚠️ país no coincide</Badge>
                          )}
                        </div>
                        <p className="text-slate-400 text-sm">
                          Vehículo: {payment.vehicle?.name || "-"}
                        </p>
                        <p className="text-slate-500 text-xs">
                          {new Date(payment.created_at).toLocaleString('es-PE')}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleViewVoucher(payment.id)}
                          variant="ghost"
                          size="sm"
                          className="text-sky-400 hover:text-sky-300"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Ver Voucher
                        </Button>
                        {payment.status === "pending" && (
                          <Button
                            onClick={() => openReviewPayment(payment)}
                            size="sm"
                            className="bg-amber-500 hover:bg-amber-600 text-white"
                          >
                            <FileText className="w-4 h-4 mr-1" />
                            Revisar
                          </Button>
                        )}
                        {payment.status !== "pending" && (
                          <Button
                            onClick={() => openReviewPayment(payment)}
                            variant="outline"
                            size="sm"
                            className="border-slate-600 text-slate-300 hover:bg-slate-700"
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Re-revisar
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* CORTE 4-C 3.1 — Gate de pago */}
          {qrGate && (
            <Card className="bg-[#14375F] border-white/15 mb-6" data-testid="qr-gate-card">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  🔒 Gate de pago (QR)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <Badge className={qrGate.can_download_qr ? "bg-[#1B84FF]/20 text-[#5BC0FF]" : "bg-red-500/20 text-red-400"} data-testid="qr-gate-status">
                    {qrGate.can_download_qr ? "✅ Puede descargar su QR" : "🚫 No puede descargar QR"}
                  </Badge>
                  <span className="text-slate-400 text-sm">{qrGate.reason}</span>
                  <Badge className="bg-amber-500/20 text-amber-400">
                    {qrGate.blocked_attempts_total} intento(s) bloqueado(s)
                  </Badge>
                </div>
                {(qrGate.blocked_attempts || []).slice(0, 10).map((b, i) => (
                  <div key={i} className="text-xs text-slate-400 py-0.5">
                    {new Date(b.at).toLocaleString('es-PE')} · <span className="text-red-300 font-mono">{b.kind}</span> · vehículo {b.vehicle_id?.slice(0, 8)}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* CORTE 4-B 4.2 — Rotaciones de QR */}
          <Card className="bg-[#14375F] border-white/15 mb-6" data-testid="qr-rotations-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <RefreshCw className="w-5 h-5" />
                Rotaciones de QR ({qrRotations.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {qrRotations.length === 0 ? (
                <p className="text-slate-400 text-sm">Este usuario no ha rotado ningún QR.</p>
              ) : (
                <div className="space-y-2">
                  {qrRotations.map((r, i) => (
                    <div key={i} className="bg-slate-700/50 rounded-lg p-3 text-sm flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`qr-rotation-row-${i}`}>
                      <span className="text-slate-300">{new Date(r.rotated_at).toLocaleString('es-PE')}</span>
                      <span className="text-white font-medium">{r.vehicle_name || r.vehicle_id?.slice(0, 8)}</span>
                      <span className="text-slate-400 font-mono text-xs">viejo: {r.old_token_masked}</span>
                      <span className="text-emerald-300 font-mono text-xs">nuevo: {r.new_token}</span>
                      <Badge className={r.actor === "admin" ? "bg-amber-500/20 text-amber-400" : "bg-sky-500/20 text-sky-400"}>
                        {r.actor === "admin" ? "Admin" : "Dueño"}
                      </Badge>
                      <span className="text-slate-500 text-xs">{r.sessions_closed} sesión(es) cerradas</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* RC-WEB-4 §30 — canonical trusted-contact + routing state */}
          <Card className="bg-[#14375F] border-white/15 mb-6" data-testid="admin-canonical-routing-card">
            <CardHeader>
              <CardTitle className="text-white text-base">Contactos de confianza y enrutamiento canónico</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(canonical?.trusted_contacts || []).length === 0 ? (
                <p className="text-sm text-slate-300" data-testid="admin-trusted-empty">
                  Sin contactos de confianza. Los avisos llegan solo al dueño.
                </p>
              ) : (
                <div className="space-y-2">
                  {(canonical?.trusted_contacts || []).map((tc) => (
                    <div key={tc.public_ref} className="rounded-lg bg-slate-700/50 p-3 text-sm text-slate-200 flex flex-wrap items-center gap-x-4 gap-y-1"
                         data-testid={`admin-trusted-row-${tc.public_ref}`}>
                      <span className="font-semibold text-white">{tc.display_name || tc.public_ref}</span>
                      <Badge className="bg-[#1B84FF]/20 text-[#5BC0FF]">{tc.status}</Badge>
                      <span>Mensajes: {tc.permissions?.receive_messages ? "sí" : "no"}</span>
                      <span>Llamadas: {tc.permissions?.receive_calls ? "sí" : "no"}</span>
                      <span className="text-slate-400">Perfiles: {(tc.vehicle_ids || []).length}</span>
                    </div>
                  ))}
                </div>
              )}
              {(canonical?.vehicles || []).map((v) => (
                <div key={`routing-${v.id}`} className="rounded-lg bg-slate-700/40 p-3 text-xs text-slate-300"
                     data-testid={`admin-routing-${v.id}`}>
                  <p className="text-white text-sm font-semibold">{v.name}</p>
                  <p>QR v{v.qr_version ?? 1} · {v.qr_status} {v.notices_paused ? "· avisos en pausa" : ""}</p>
                  <p>Mensajes → {v.routing?.message_recipient_count ?? 0} destinatario(s): {(v.routing?.message_recipients || []).join(", ") || "—"}</p>
                  <p>Llamadas → {v.routing?.call_recipient_count ?? 0} destinatario(s): {(v.routing?.call_recipients || []).join(", ") || "—"}</p>
                  {(v.legacy_contact_data?.primary_contact || v.legacy_contact_data?.emergency_contact_1) && (
                    <p className="mt-1 text-amber-300" data-testid={`admin-legacy-contact-${v.id}`}>
                      Dato de contacto heredado (legacy, no se usa para enrutar): {v.legacy_contact_data.primary_contact || v.legacy_contact_data.emergency_contact_1}
                    </p>
                  )}
                </div>
              ))}
              {canonical?.notification_settings && (
                <div className="rounded-lg bg-slate-700/40 p-3 text-xs text-slate-300" data-testid="admin-notification-settings">
                  Preferencias del usuario (solo lectura): mensajes {canonical.notification_settings.messages_enabled ? "sí" : "no"} ·
                  llamadas {canonical.notification_settings.calls_enabled ? "sí" : "no"} ·
                  avisos urgentes {canonical.notification_settings.urgent_reminders_enabled ? "sí" : "no"} ·
                  navegador {canonical.notification_settings.browser_push_subscriptions ?? 0} ·
                  app {canonical.notification_settings.app_devices_registered ?? 0}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Vehicles */}
          <h2 className="text-xl font-bold text-white mb-4">Vehículos ({vehicles.length})</h2>
          
          {vehicles.length === 0 ? (
            <Card className="bg-[#14375F] border-white/15">
              <CardContent className="py-12 text-center">
                <Car className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">Este usuario no tiene vehículos registrados</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {vehicles.map((vehicle) => (
                <Card key={vehicle.id} className="bg-[#14375F] border-white/15">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-white">{vehicle.name}</h3>
                        <p className="text-slate-400 font-mono">Placa: {vehicle.plate_number}</p>
                        <Badge className={
                          vehicle.qr_status === "active" ? "bg-[#1B84FF]/20 text-[#5BC0FF] mt-2" :
                          vehicle.qr_status === "inactive" ? "bg-red-500/20 text-red-400 mt-2" :
                          "bg-yellow-500/20 text-yellow-400 mt-2"
                        }>
                          QR: {vehicle.qr_status}
                        </Badge>
                      </div>
                      <Button onClick={() => openEditVehicle(vehicle)} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">
                        <Edit className="w-4 h-4 mr-2" />
                        Editar Vehículo
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div className="bg-slate-700/50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Phone className="w-4 h-4 text-[#5BC0FF]" />
                          <span className="text-sm text-slate-400">Destinatarios de avisos</span>
                        </div>
                        <p className="text-white font-mono">{getContactValue(vehicle.primary_contact)}</p>
                      </div>
                      {/* iter65 — Contactos de emergencia PURGADOS de la vista admin */}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Edit User Dialog */}
      <Dialog open={editUserOpen} onOpenChange={setEditUserOpen}>
        <DialogContent className="bg-[#14375F] border-white/15 text-white">
          <DialogHeader>
            <DialogTitle>Editar Usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre Completo</Label>
              <Input
                value={editUserData.full_name || ""}
                onChange={(e) => setEditUserData({...editUserData, full_name: e.target.value})}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div>
              <Label>Teléfono</Label>
              <Input
                value={editUserData.phone || ""}
                onChange={(e) => setEditUserData({...editUserData, phone: e.target.value})}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div>
              <Label>WhatsApp</Label>
              <Input
                value={editUserData.whatsapp || ""}
                onChange={(e) => setEditUserData({...editUserData, whatsapp: e.target.value})}
                className="bg-slate-700 border-slate-600"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserOpen(false)} className="border-slate-600">
              Cancelar
            </Button>
            <Button onClick={saveUser} disabled={savingUser} className="bg-[#1B84FF] hover:bg-[#0F6FDB]">
              {savingUser ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Vehicle Dialog */}
      <Dialog open={editVehicleOpen} onOpenChange={setEditVehicleOpen}>
        <DialogContent className="bg-[#14375F] border-white/15 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Vehículo y Contactos</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Vehicle Info */}
            <div className="space-y-4">
              <h3 className="font-semibold text-[#5BC0FF]">Información del Vehículo</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nombre</Label>
                  <Input
                    value={editVehicleData.name || ""}
                    onChange={(e) => setEditVehicleData({...editVehicleData, name: e.target.value})}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <Label>Placa</Label>
                  <Input
                    value={editVehicleData.plate_number || ""}
                    onChange={(e) => setEditVehicleData({...editVehicleData, plate_number: e.target.value})}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>
            </div>

            {/* Primary Contact */}
            <div className="space-y-4">
              <h3 className="font-semibold text-[#5BC0FF]">Destinatarios de avisos</h3>
              <div>
                <Label>Teléfono Principal</Label>
                <Input
                  value={editVehicleData.primary_contact || ""}
                  onChange={(e) => setEditVehicleData({...editVehicleData, primary_contact: e.target.value})}
                  className="bg-slate-700 border-slate-600"
                  placeholder="987654321"
                />
              </div>
            </div>

            {/* iter65 — Contactos de emergencia PURGADOS del editor admin */}
            <div className="bg-[#1B84FF]/10 border border-emerald-500/30 rounded-lg p-4">
              <p className="text-[#5BC0FF] text-sm">
                ✓ Los cambios se reflejarán inmediatamente cuando alguien escanee el QR del vehículo.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditVehicleOpen(false)} className="border-slate-600">
              Cancelar
            </Button>
            <Button onClick={saveVehicle} disabled={savingVehicle} className="bg-[#1B84FF] hover:bg-[#0F6FDB]">
              {savingVehicle ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="bg-[#14375F] border-white/15 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedPayment?.status === "pending" ? "Revisar Pago" : "Re-revisar Pago"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Payment Details */}
            <div className="bg-slate-700 p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-400">Monto</p>
                  <p className="text-white font-semibold">S/ {selectedPayment?.amount}</p>
                </div>
                <div>
                  <p className="text-slate-400">Método</p>
                  <p className="text-white font-semibold capitalize">{selectedPayment?.method}</p>
                </div>
                <div>
                  <p className="text-slate-400">Vehículo</p>
                  <p className="text-white">{selectedPayment?.vehicle?.name || "-"}</p>
                </div>
                <div>
                  <p className="text-slate-400">Estado Actual</p>
                  <Badge className={
                    selectedPayment?.status === "approved" ? "bg-[#1B84FF]/20 text-[#5BC0FF]" :
                    selectedPayment?.status === "rejected" ? "bg-red-500/20 text-red-400" :
                    "bg-amber-500/20 text-amber-400"
                  }>
                    {selectedPayment?.status === "approved" ? "Aprobado" :
                     selectedPayment?.status === "rejected" ? "Rechazado" : "Pendiente"}
                  </Badge>
                </div>
              </div>
            </div>

            {/* View Voucher Button */}
            <Button
              variant="outline"
              onClick={() => handleViewVoucher(selectedPayment?.id)}
              className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              <ZoomIn className="w-4 h-4 mr-2" />
              Ver Voucher Completo
            </Button>
            
            {/* Admin Notes */}
            <div className="space-y-2">
              <Label className="text-slate-400">Notas internas (opcional)</Label>
              <Textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Notas para registro interno..."
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>

            {/* Rejection Reason */}
            <div className="space-y-2">
              <Label className="text-slate-400">Razón de rechazo (requerido si rechaza)</Label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Ej: Voucher borroso, monto incorrecto, nombre no coincide..."
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => submitReview("rejected")}
              disabled={reviewing}
              className="border-red-500 text-red-400 hover:bg-red-500/20"
            >
              <Ban className="w-4 h-4 mr-2" />
              Rechazar
            </Button>
            <Button
              onClick={() => submitReview("approved")}
              disabled={reviewing}
              className="bg-[#1B84FF] hover:bg-[#0F6FDB] text-white"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Aprobar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Voucher Dialog */}
      <Dialog open={voucherDialogOpen} onOpenChange={setVoucherDialogOpen}>
        <DialogContent className="bg-[#14375F] border-white/15 max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-white">Comprobante de Pago</DialogTitle>
          </DialogHeader>
          {voucherUrl && (
            <div className="flex justify-center overflow-auto max-h-[70vh]">
              <img src={voucherUrl} alt="Voucher" className="max-w-full rounded-lg" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoucherDialogOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUserDetail;
